// Parsing helpers for V2 workflow step output.
//
// Two real-world quirks the V2 executor throws at us:
//  1. `workflow_data` is sometimes a JSON *string* rather than an object (seen on large
//     payloads, e.g. a 20-match cortese run ~9KB).
//  2. Python handlers serialize missing values as bare `NaN` / `Infinity`, which are NOT
//     valid JSON. `JSON.parse` throws on them, which previously blanked the entire result
//     (a cortese run with 3 real matches rendered nothing because two `case_type` fields
//     came through as `NaN`).
//
// parseLooseJson tolerates (2) by replacing those bare literals with null before parsing,
// so one bad field can't take down a whole result set.

/** Replace bare NaN / Infinity / -Infinity literals (invalid JSON) with null. */
export function sanitizeJsonLiterals(raw: string): string {
  // only match when the literal sits in a value position (after ':' or ',' or '['),
  // so we never corrupt these words inside a legitimate string value.
  return raw.replace(/([:[,]\s*)(-?\bInfinity\b|\bNaN\b)/g, '$1null');
}

/** JSON.parse that tolerates Python-style NaN/Infinity. Returns undefined if unparseable. */
export function parseLooseJson<T = any>(raw: string): T | undefined {
  try {
    return JSON.parse(raw) as T;
  } catch {
    try {
      return JSON.parse(sanitizeJsonLiterals(raw)) as T;
    } catch {
      return undefined;
    }
  }
}

/**
 * Normalize `output.workflow_data` into an object.
 * Handles both the object form and the stringified form (incl. NaN/Infinity).
 */
export function normalizeWorkflowData(wd: any): Record<string, any> | undefined {
  let value = wd;
  if (typeof value === 'string') {
    value = parseLooseJson(value);
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, any>;
}
